import moment from 'moment';
import { getMomentFromTimestamp } from '../../utils';

export const chartLineColors = [
  '#33cdff',
  '#66db34',
  '#bd72ff',
  '#fb901e',
  '#ff4947',
];

export function getChartLineColor(colorIndex) {
  const color = chartLineColors[colorIndex] || chartLineColors[0];

  return color;
}

export function useChartLineColor(colorIndex = 0) {
  const color = getChartLineColor(colorIndex);

  return color;
}

export function timeToTz(originalTime) {
  const d = new Date(
    new Date(originalTime).toLocaleString('en-US', {
      timeZone: 'Asia/Calcutta',
    })
  );

  return (
    Date.UTC(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      d.getHours(),
      d.getMinutes(),
      d.getSeconds(),
      d.getMilliseconds()
    ) / 1000
  );
}

export function generateMockOhlcPerSec() {
  const startPrice = 50;
  const volatility = 20;
  const current = moment()
    .utc()
    .utcOffset('+05:30')
    .startOf('day')
    .add(9, 'hours');

  const todayEnd = moment()
    .utc()
    .utcOffset('+05:30')
    .startOf('day')
    .add(15, 'hours');

  const data = [];
  let prevPrice = startPrice;

  while (current.isBefore(todayEnd)) {
    const change = Math.random() * volatility * 2 - volatility;
    const price = prevPrice + change;
    prevPrice = price;

    data.push({
      time: timeToTz(current.unix()),
      open: price,
      high: price,
      low: price,
      close: price,
    });

    current.add(1, 'minute');
  }

  return data;
}

export function getStatsFromOhlc(ohlcData = []) {
  const newData = [];
  let minPnl = 0;
  let maxPnl = 0;
  let totalPnl = 0;

  for (let index = 0; index < ohlcData.length; index++) {
    const element = ohlcData[index];
    const close = element.close;

    totalPnl += close;

    if (close < minPnl) {
      minPnl = close;
    }

    if (close > maxPnl) {
      maxPnl = close;
    }

    newData.push({ value: element.close, time: element.time });
  }

  return {
    lineData: newData,
    minPnl,
    maxPnl,
    totalPnl,
  };
}

export function getStatsFromData(data = []) {
  let minPnl = 0;
  let maxPnl = 0;
  let totalPnl = 0;

  for (let index = 0; index < data.length; index++) {
    const element = data[index];
    let price = undefined;

    if (element.value !== undefined) {
      price = element.value;
    } else if (element.close !== undefined) {
      price = element.close;
    }

    if (price === undefined) {
      continue;
    }

    totalPnl += price;

    if (price < minPnl) {
      minPnl = price;
    }

    if (price > maxPnl) {
      maxPnl = price;
    }
  }

  return {
    minPnl,
    maxPnl,
    totalPnl,
  };
}

// data = [
//   {
//     "_id": "666c0cdcfcbc5aeb5b3295a2",
//     "details": "Algo 6|mastertrustkambala:7MAA525",
//     "count": 5,
//     "ohlcData": [
//         "1718357460000,0.00,0.00,0.00,0.00",
//         "1718357400000,0.00,0.00,0.00,0.00",
//         "1718357340000,0.00,0.00,0.00,0.00",
//         "1718357280000,0.00,0.00,0.00,0.00",
//         "1718357220000,0.00,0.00,0.00,0.00"
//     ]
// }
// ]

export function getEntryTimeEdges(data) {
  let firstEntryTime = null;
  let lastEntryTime = null;

  for (let index = 0; index < data.length; index++) {
    const element = data[index];

    // get first and last time
    if (!firstEntryTime) {
      firstEntryTime = element.time;
    }

    if (element.time < firstEntryTime) {
      firstEntryTime = element.time;
    }

    if (!lastEntryTime) {
      lastEntryTime = element.time;
    }

    if (element.time > lastEntryTime) {
      lastEntryTime = element.time;
    }
  }

  return {
    firstEntryTime,
    lastEntryTime,
  };
}

export function convertDataToTradingData(dataRaw) {
  const newData = [...dataRaw];

  const ohlcData = [];

  for (let index = 0; index < newData.length; index++) {
    const data = newData[index];
    const [t, o, h, l, c] = data.split(',');

    const time = timeToTz(Number(t));
    const open = parseFloat(o);
    const high = parseFloat(h);
    const low = parseFloat(l);
    const close = parseFloat(c);

    ohlcData.push({
      time,
      open,
      high,
      low,
      close,
    });
  }

  const sortedOhlcData = ohlcData.sort((a, b) => a.time - b.time);

  return sortedOhlcData;
}

export function convertCsvToTradingData(feed) {
  const [, , t, o, h, l, c] = feed.split(',');

  const time = timeToTz(Number(t));
  const open = parseFloat(o);
  const high = parseFloat(h);
  const low = parseFloat(l);
  const close = parseFloat(c);

  const data = {
    open,
    high,
    low,
    close,
    time,
  };

  return data;
}

export function sumTradingData(ohlcDataRaw, name = 'summed-data') {
  const newData = ohlcDataRaw;

  const allOhlcData = [];

  for (let index = 0; index < newData.length; index++) {
    const element = newData[index];

    for (let index2 = 0; index2 < element.ohlcData.length; index2++) {
      const data = element.ohlcData[index2];

      const targetIndex = allOhlcData.findIndex(
        (item) => item.time === data.time
      );

      if (targetIndex < 0) {
        allOhlcData.push({
          open: data.open,
          high: data.high,
          low: data.low,
          close: data.close,
          time: data.time,
        });

        continue;
      }

      const dataInternal = allOhlcData[targetIndex];

      const open = dataInternal.open + data.open;
      const high = dataInternal.high + data.high;
      const low = dataInternal.low + data.low;
      const close = dataInternal.close + data.close;

      if (isNaN(open) || isNaN(high) || isNaN(low) || isNaN(close)) {
        continue;
      }

      allOhlcData[targetIndex] = {
        time: data.time,
        open,
        high,
        low,
        close,
      };
    }
  }

  const mtmCounts = {
    total: 0,
    min: 0,
    max: 0,
  };

  for (let index = 0; index < allOhlcData.length; index++) {
    const data = allOhlcData[index];
    const close = data.close;

    mtmCounts.total += data.close;

    if (mtmCounts.min === 0 || close < mtmCounts.min) {
      mtmCounts.min = close;
    }

    if (close > mtmCounts.max) {
      mtmCounts.max = close;
    }
  }

  const sortedOhlcData = allOhlcData.sort((a, b) => a.time - b.time);

  return {
    data: [
      {
        _id: name,
        ohlcData: sortedOhlcData,
        mtmCounts,
      },
    ],
    mtmCounts,
  };
}

export function getStatsFromTradingData(data) {
  let min = 0;
  let max = 0;

  for (let index = 0; index < data.length; index++) {
    const element = data[index];
    const low = element.low;
    const high = element.high;

    if (low < min) {
      min = low;
    }

    if (high > max) {
      max = high;
    }
  }

  return {
    min,
    max,
  };
}

export function combineData(oldDataRaw, responseDataRaw) {
  const oldData = oldDataRaw.data || [];
  const newData = [...oldData];

  for (let index = 0; index < responseDataRaw.data.length; index++) {
    const newDataItem = responseDataRaw.data[index];

    // check if already exits
    const isAlgoExists = newData.some((item) => item._id === newDataItem._id);

    // append new entry
    if (!isAlgoExists) {
      newData.push(newDataItem);
      continue;
    }

    const oldDataIndex = newData.findIndex(
      (item) => item._id === newDataItem._id
    );

    if (oldDataIndex < 0) {
      continue;
    }

    const newOhlcs = [...newData[oldDataIndex].ohlcData];

    // avoid duplicates
    for (let index3 = 0; index3 < newDataItem.ohlcData.length; index3++) {
      const newDataItemOhlcData = newDataItem.ohlcData[index3];
      const isExists = newOhlcs.some(
        (item) => item.time === newDataItemOhlcData.time
      );

      if (!isExists) {
        newOhlcs.push(newDataItemOhlcData);
      }
    }
    // caclculate total
    const mtmCounts = {
      total: 0,
      min: 0,
      max: 0,
    };

    for (let index2 = 0; index2 < newOhlcs.length; index2++) {
      const data = newOhlcs[index2];

      mtmCounts.total += data.close;

      if (data.close < mtmCounts.min) {
        mtmCounts.min = data.close;
      }

      if (data.close > mtmCounts.max) {
        mtmCounts.max = data.close;
      }
    }

    const sortedOhlcData = newOhlcs.sort((a, b) => a.time - b.time);
    newData[oldDataIndex].ohlcData = sortedOhlcData;
    newData[oldDataIndex].lastFeed = sortedOhlcData[sortedOhlcData.length - 1];
  }

  // total and firstEntryTime, lastEntryTime
  const mtmCountsTotal = {
    total: 0,
    min: 0,
    max: 0,
  };

  let firstEntryTime = null;
  let lastEntryTime = null;

  for (let index = 0; index < newData.length; index++) {
    const item = newData[index];

    for (let index2 = 0; index2 < item.ohlcData.length; index2++) {
      const ohlc = item.ohlcData[index2];

      // total
      mtmCountsTotal.total += ohlc.close;

      if (ohlc.close < mtmCountsTotal.min) {
        mtmCountsTotal.min = ohlc.close;
      }

      if (ohlc.close > mtmCountsTotal.max) {
        mtmCountsTotal.max = ohlc.close;
      }

      if (!firstEntryTime) {
        firstEntryTime = ohlc.time;
      }

      if (!lastEntryTime) {
        lastEntryTime = ohlc.time;
      }

      if (ohlc.time < firstEntryTime) {
        firstEntryTime = ohlc.time;
      }

      if (ohlc.time > lastEntryTime) {
        lastEntryTime = ohlc.time;
      }
    }
  }

  return {
    responseData: {
      data: newData,
      mtmCounts: mtmCountsTotal,
    },
    firstEntryTime,
    lastEntryTime,
  };
}

//

export function convertUnderlyingDataToTradingData(dataRaw) {
  const ohlcData = [];

  for (let index1 = 0; index1 < dataRaw.length; index1++) {
    const data = dataRaw[index1];
    const [t, o, h, l, c] = data.split(',');

    const time = timeToTz(Number(t));
    const open = parseFloat(o);
    const high = parseFloat(h);
    const low = parseFloat(l);
    const close = parseFloat(c);

    ohlcData.push({
      time,
      open,
      high,
      low,
      close,
    });
  }

  const sortedOhlcData = ohlcData.sort((a, b) => a.time - b.time);

  return sortedOhlcData;
}

export function combineUnderlyingData(oldDataRaw, responseDataRaw) {
  const newOhlc = [...oldDataRaw];

  for (let index = 0; index < responseDataRaw.length; index++) {
    const resItemOhlc = responseDataRaw[index];
    const isExists = newOhlc.some((item) => item.time === resItemOhlc.time);

    if (isExists) {
      continue;
    }

    newOhlc.push(resItemOhlc);
  }

  const sortedOhlcData = newOhlc.sort((a, b) => a.time - b.time);

  return sortedOhlcData;
}

export function convertUnderlyingCsvToTradingData(feed) {
  const [, t, o, h, l, c] = feed.split(',');

  const time = timeToTz(Number(t));
  const open = parseFloat(o);
  const high = parseFloat(h);
  const low = parseFloat(l);
  const close = parseFloat(c);

  const data = {
    open,
    high,
    low,
    close,
    time,
  };

  return [data];
}

export function getLastValues(lastValues1 = {}, pnlFeed = []) {
  const newLastValues = {
    ...lastValues1,
  };

  for (let index = 0; index < pnlFeed.length; index++) {
    const feed = pnlFeed[index];

    const [, algoId, stateId, t, o, h, l, c] = feed.split(',');

    const timeMoment = getMomentFromTimestamp(Number(t));
    const open = parseFloat(o);
    const high = parseFloat(h);
    const low = parseFloat(l);
    const close = parseFloat(c);

    newLastValues[stateId] = {
      algo: algoId,
      t: timeMoment.toISOString(),
      o: open,
      h: high,
      l: low,
      c: close,
      state: stateId,
    };
  }

  return newLastValues;
}

export function sumLiveTrade(lastValues = {}) {
  let open = 0;
  let high = 0;
  let low = 0;
  let close = 0;
  let time = null;

  for (const [, value] of Object.entries(lastValues)) {
    const timeMoment = getMomentFromTimestamp(value.t).valueOf();

    open += value.o;
    high += value.h;
    low += value.l;
    close += value.c;

    if (time === null) {
      time = timeMoment;
    }

    if (timeMoment > time) {
      time = timeMoment;
    }
  }

  if (time === null) {
    return [];
  }

  const timeFormatted = timeToTz(time);

  return [
    {
      open,
      high,
      low,
      close,
      time: timeFormatted,
    },
  ];
}

export function combineTradeData(oldData, responseData) {
  const newData = [...oldData];

  for (let index = 0; index < responseData.length; index++) {
    const newDataItemOhlcData = responseData[index];
    const targetIndex = newData.findIndex(
      (item) => item.time === newDataItemOhlcData.time
    );

    if (targetIndex < 0) {
      newData.push(newDataItemOhlcData);

      continue;
    }

    newData[targetIndex] = {
      ...newDataItemOhlcData,
    };
  }

  const sortedOhlcData = newData.sort((a, b) => a.time - b.time);

  return sortedOhlcData;
}
